import React, { useEffect, useMemo, useState } from 'react';

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

export default function MemberSelect({ items, value, onChange }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  const matches = useMemo(() => {
    const needle = normalize(query);
    if (!needle) return [];
    return (items || [])
      .filter(person => normalize(person.name).includes(needle))
      .slice(0, 10);
  }, [items, query]);

  const current = (items || []).find(person => person.name === value);

  const choose = person => {
    setQuery(person.name);
    setOpen(false);
    setActiveIndex(-1);
    onChange(person.name);
  };

  const handleChange = event => {
    const next = event.target.value;
    setQuery(next);
    setOpen(Boolean(next.trim()));
    setActiveIndex(-1);
    if (value && next !== value) onChange('');
  };

  const handleKeyDown = event => {
    if (!open || matches.length === 0) {
      if (event.key === 'Escape') setOpen(false);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(index => Math.min(matches.length - 1, index + 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(index => Math.max(0, index - 1));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const exact = matches.find(person => normalize(person.name) === normalize(query));
      const selected = exact || matches[Math.max(0, activeIndex)];
      if (selected) choose(selected);
      return;
    }

    if (event.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div className="pub-select">
      <div className="pub-typeahead">
        <label htmlFor="member-search">Member</label>
        <input
          id="member-search"
          type="search"
          value={query}
          placeholder="Start typing a name"
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open && matches.length > 0}
          aria-controls="member-search-results"
          aria-activedescendant={activeIndex >= 0 ? `member-option-${activeIndex}` : undefined}
          onChange={handleChange}
          onFocus={() => setOpen(Boolean(query.trim()))}
          onKeyDown={handleKeyDown}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        />

        {open && query.trim() && (
          <div className="pub-typeahead-menu" id="member-search-results" role="listbox">
            {matches.length > 0 ? matches.map((person, index) => (
              <button
                type="button"
                id={`member-option-${index}`}
                key={`${person.employeeId || person.name}-${person.name}`}
                className={`pub-typeahead-option${index === activeIndex ? ' is-active' : ''}`}
                role="option"
                aria-selected={index === activeIndex}
                onMouseDown={event => event.preventDefault()}
                onClick={() => choose(person)}
              >
                <strong>{person.name}</strong>
                <span>{person.rank || 'Rank unavailable'}</span>
              </button>
            )) : (
              <div className="pub-typeahead-empty">No matching members in the current review list.</div>
            )}
          </div>
        )}
      </div>
      <div className="pub-rank">{current ? current.rank : value ? 'Loading member detail…' : 'Search by first or last name.'}</div>
    </div>
  );
}
